import { useState, useEffect, useRef, useMemo } from 'react'
import styles from './LiveAuctionPage.module.css'
import { useParams } from 'react-router'
import { useAutoEvent } from '../../queries/useAutoEvent'
import { useEventState } from '../../slices/eventSlice'
import {
  useFetchLiveAuctionItems,
  useUpdateLiveBid,
  useClearLiveBid,
  useConfirmLiveAuction
} from '../../queries/useActionItemsFetch'
import {
  DataGridPro,
  GridCellParams,
  useGridApiRef,
  GridValueFormatterParams
} from '@mui/x-data-grid-pro'
import Box from '@mui/material/Box';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText
} from '@mui/material'
import { formatMoney } from '../../util/formatHelpers'
import { Autocomplete, TextField } from '@mui/material'
import clsx from 'clsx';

interface Params {
  eventid: string
}

type selectOption = {
  label: string
  value: number
}

const LiveAuctionPage = () => {
  const { eventid } = useParams<Params>()

  useAutoEvent(eventid)
  const eventState = useEventState()
  const { data, isLoading } = useFetchLiveAuctionItems(Number(eventid))
  const manualBidMutation = useUpdateLiveBid()
  const clearBidMutation = useClearLiveBid()
  const [liveBidError, setLiveBidError] = useState('')
  const [rows, setRows] = useState([])
  const [participantList, setParticipantList] = useState([])
  const apiRef = useGridApiRef()
  const [clearedRowIds, setClearedRowIds] = useState([])
  const confirmmutation = useConfirmLiveAuction()
  const [dialogMsg, setDialogMsg] = useState('')
  const editableFields = ['bidderparticipantid', 'lastbid']
  const lastCommittedRowRef = useRef(null);
  const [isLoadingSpinner, setIsLoading] = useState(false);
  const [unsavedRowIds, setUnsavedRowIds] = useState<Set<number>>(new Set());
  const [pendingEditValues, setPendingEditValues] = useState<Record<number, { bidderparticipantid?: number, lastbid?: number }>>({});


  useEffect(() => {
    if (data && data.LiveAuctionitemlist) {
      setRows(data.LiveAuctionitemlist)
      setParticipantList(data.bidderlist)

      const blankRowIds = data.LiveAuctionitemlist.filter(
        (row) => row.bidderparticipantid <= 0
      ).map((row) => row.itemid)
      setClearedRowIds(blankRowIds)
    }
  }, [data])

  const bidderOptions = useMemo<selectOption[]>(
      () => participantList?.map((item) => {
        return { value: item.bidderparticipantid, label: item.biddername }
      }) ?? [],
      [participantList]
  )
  const getBidderOptions = () => bidderOptions
  const handleRowEditCommit = (newRow, oldRow) => {
    const updatedRow = { ...newRow, isNew: false }
    lastCommittedRowRef.current = updatedRow
    setUnsavedRowIds(prev => new Set(prev).add(newRow.itemid))
    return updatedRow
  }
  const handleRowUpdateError = (error) => {
    console.log(error)
  }
  const handleSave = (row) => {
    setClearedRowIds((prevIds) => prevIds.filter((id) => id !== row.itemid))
      try {
        console.log('mutate')
        var part = participantList.find(
          (p) => p.bidderparticipantid === row.bidderparticipantid
        )
        manualBidMutation.mutate(
          {
            eventid: eventState.eventId,
            itemid: row.itemid,
            amount: row.lastbid,
            bidderparticipantid: part.bidderparticipantid
          },
          {
            onSuccess: (response) => {
              const json = JSON.parse(response.data.jsonString)
              console.log(json)
              if (json.status === 'failed') {
                setLiveBidError(json.message)
              } else {
                setLiveBidError('')
              }
              setIsLoading(false);
              setUnsavedRowIds(prev => { const next = new Set(prev); next.delete(row.itemid); return next; });
              setPendingEditValues(prev => { const next = { ...prev }; delete next[row.itemid]; return next; });
            }
          }
        )
      } catch {
        setIsLoading(false);
      }
    
  }
  const handleClear = (row) => {
    setClearedRowIds((prevIds) => [...prevIds, row.itemid])

    clearBidMutation.mutate(
      {
        eventid: eventState.eventId,
        historyid: row.historyid
      },
      {
        onSuccess: (data) => {
          const json = JSON.parse(data.data.jsonString)
          console.log(json)
          if (json.status === 'failed') {
            setLiveBidError(json.message)
          } else {
            setLiveBidError('')
          }
        }
      }
    )
  }
  
  const onButtonClick = (e, cellParams) => {
    const isSaveButton = clearedRowIds.includes(cellParams.row.itemid)
    if (isSaveButton) {
      if (apiRef.current.getRowMode(cellParams.row.itemid) === 'edit') {
        apiRef.current.stopRowEditMode({ id: cellParams.row.itemid });
      }
      const base = lastCommittedRowRef.current?.itemid === cellParams.row.itemid
        ? lastCommittedRowRef.current
        : cellParams.row
      const pending = pendingEditValues[cellParams.row.itemid] ?? {}
      lastCommittedRowRef.current = null
      handleSave({ ...base, ...pending })
    } else {
      handleClear(cellParams.row)
    }
  }

  const sendWinningBid = () => {
    confirmmutation.mutate(
      {
        eventid: eventState.eventId
      },
      {
        onSuccess: (data) => {
          const json = JSON.parse(data.data.jsonString)
          if (json.status === 'failure') {
            setDialogMsg(json.message)
          } else {
            setDialogMsg(
              'Successfully notified winning bidders and added the items to their carts. '
            )
          }
        }
      }
    )
  }
 
  const columns = [
    {
      field: 'itemnumber',
      type: 'string',
      headerName: 'Item #',
      width: 150
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 300,
      sortable: false
    },
    {
      field: 'bidderparticipantid',
      headerName: 'Bidder',
      width: 300,
      sortable: false,
      editable: true,
      renderEditCell: (params) => (
        <Autocomplete
          fullWidth
          options={bidderOptions}
          filterOptions={(opts, state) => {
            const inputValue = state.inputValue.toLowerCase()
            return opts.filter((option) =>
                option.label?.toLowerCase().includes(inputValue)
            )
          }}
          getOptionLabel={(option: selectOption) => option?.label ?? ''}
          autoHighlight
          openOnFocus
          componentsProps={{ popper: { style: { zIndex: 9999 } } }}
          value={bidderOptions.find((op) => op.value === params.value) ?? null}
          isOptionEqualToValue={(op, val) => op.value === val?.value}
          renderOption={(props, option: selectOption) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
          )}
          onChange={(event, newValue) => {
            if (typeof newValue === 'object' && newValue !== null) {
              apiRef.current.setEditCellValue({
                id: params.id,
                field: params.field,
                value: newValue.value
              })
              setPendingEditValues(prev => ({
                ...prev,
                [params.row.itemid]: { ...prev[params.row.itemid], bidderparticipantid: newValue.value }
              }))
            }
          }}
          renderInput={(inputParams) => (
            <TextField {...inputParams} autoFocus={params.hasFocus} error={params.error} />
          )}
        />
      ),
      valueFormatter: (params: GridValueFormatterParams) => {
        var bidderOptions = getBidderOptions();
        if (bidderOptions && bidderOptions.length > 0) {
          var part = bidderOptions.find((p) => p.value === params.value);
          return part ? part.label : '(Click to add bidder)';
        } else {
          return '';
        }
      } ,
      cellClassName: (params: GridCellParams<any>) => {
        console.log(params)
        if (params.value == null) {
          return '';
        }
  
        return clsx('super-app', {
          negative: params.value < 0,
        });
      } 
    },
    {
      field: 'lastbid',
      headerName: 'Amount',
      width: 150,
      type: 'number',
      valueFormatter: (params: GridValueFormatterParams) => {
        return formatMoney(Number(params.value ? params.value : 0))
      },
      editable: true,
      renderEditCell: (params) => (
        <TextField
          type="number"
          autoFocus={params.hasFocus}
          defaultValue={params.value ?? ''}
          onChange={(e) => {
            const value = Number(e.target.value)
            apiRef.current.setEditCellValue({ id: params.id, field: params.field, value })
            setPendingEditValues(prev => ({
              ...prev,
              [params.row.itemid]: { ...prev[params.row.itemid], lastbid: value }
            }))
          }}
          onFocus={(e) => e.target.select()}
          inputProps={{ style: { textAlign: 'right' } }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 150,
      renderCell: (params) => {
        if (params.row.livebidcheckedout == 'Yes') {
          return null
        }
        const isCleared = clearedRowIds.includes(params.row.itemid)
        if (isCleared) {
          const pending = pendingEditValues[params.row.itemid]
          const effectiveAmount = pending?.lastbid ?? params.row.lastbid
          const canSave = pending?.bidderparticipantid > 0 && effectiveAmount > 0
          if (!canSave) return null
          return (
            <Button onClick={(e) => onButtonClick(e, params)} variant="contained">
              Save Bid
            </Button>
          )
        }
        return (
          <Button onClick={(e) => onButtonClick(e, params)} variant="contained">
            Clear Bid
          </Button>
        )
      }
    },
    {
      field: 'livebidconfirmed',
      headerName: 'Added to Cart',
      width: 150,
      sortable: false
    },
    {
      field: 'livebidcheckedout',
      headerName: 'Checked Out',
      width: 150,
      sortable: false
    },
    {
      field: 'livebidhasemail',
      headerName: 'Has Email',
      width: 150,
      sortable: false
    },
  ]

 

  const closeDialog = () => {
    setDialogMsg('')
  }
  return (
    <Box
    sx={{
      
      '& .super-app.negative': {
        color: '#a9a9a9',
        fontWeight: '600',
      }
    }}
  >
      <>
        <Dialog
          open={dialogMsg.length > 0}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description">
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {dialogMsg}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Ok</Button>
          </DialogActions>
        </Dialog>
      </>
      <h2>Live Auction Winning Bid Entry</h2>
      <h4>Click to enter winning bidder and amount.</h4>
      {liveBidError.length > 0 && <p>An error occurred: {liveBidError}</p>}
      <Button
        variant="contained"
        color="primary"
        style={{ marginBottom: '20px' }}
        onClick={() => sendWinningBid()}>
        Send Winning Bid Emails and Add Items to Cart
      </Button>
      <DataGridPro
        apiRef={apiRef}
        editMode="row"
        autoHeight
        getRowId={(row) => row.itemid}
        columns={columns}
        rows={rows}
        loading={isLoading}
        rowThreshold={0}
        processRowUpdate={handleRowEditCommit}
        onProcessRowUpdateError={handleRowUpdateError}
        experimentalFeatures={{ newEditingApi: true }}
        isCellEditable={(params) => clearedRowIds.includes(params.row.itemid)}
        onCellClick={(params) => {
          if (editableFields.includes(params.field) && clearedRowIds.includes(params.row.itemid) && apiRef.current.getRowMode(params.id) !== 'edit') {
            apiRef.current.startRowEditMode({ id: params.id, fieldToFocus: params.field });
          }
        }}
        getRowClassName={(params) => unsavedRowIds.has(params.row.itemid) ? styles.unsavedRow : ''}
      />
    </Box>
  )
}

export default LiveAuctionPage
